import { useEffect, useRef } from 'react';
import { message } from 'antd';
import { useToken } from '../common/helper';
import { GetWsTicket } from '../apis/auth';
import { getSessionSignal } from '@/common/interceptor/sessionAbortController';

export interface AICallStatusData {
  type: string;
  status: string;
  to_number: string;
  message: string;
  triggered_by: string;
  event_uuid?: string;
}

/**
 * Hook for establishing WebSocket connection to receive AI call status notifications
 *
 * Displays toast notifications when calls are delayed due to:
 * - Concurrency limits (warning)
 * - Phone number busy (info)
 */
export default function useAICallStatusSocket(): void {
  const { token, decodedUserToken } = useToken();

  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!token) return;

    const decoded = decodedUserToken?.();
    if (decoded?.is_preview) {
      wsRef.current?.close();
      return;
    }

    const baseDomain = process.env.REACT_APP_WEB_SOCKETS;
    const signal = getSessionSignal();
    let cancelled = false;

    const onAbort = () => {
      cancelled = true;
      wsRef.current?.close();
    };

    signal.addEventListener('abort', onAbort, { once: true });

    const connect = async () => {
      let url: string;
      try {
        const res = await GetWsTicket();
        const ticket = res?.data?.ticket;
        if (cancelled || signal.aborted) return;
        url = `${baseDomain}/ai-call-status/?ticket=${ticket}`;
      } catch (error: unknown) {
        const err = error as { code?: string; name?: string; message?: string };
        if (
          cancelled ||
          signal.aborted ||
          err?.code === 'ERR_CANCELED' ||
          err?.name === 'CanceledError' ||
          err?.message?.toLowerCase().includes('canceled') ||
          err?.message?.toLowerCase().includes('cancelled')
        ) {
          return;
        }
        url = `${baseDomain}/ai-call-status/?token=${token}`;
      }
      if (cancelled || signal.aborted) return;

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('AI Call Status WebSocket connected');
      };

      ws.onmessage = (event) => {
        console.log('AI Call Status message received:', event.data);
        const data = JSON.parse(event.data) as AICallStatusData;
        console.log('Parsed data:', data);

        if (data.type === 'ai_call_status_notification') {
          console.log('Notification type matches, status:', data.status);
          if (data.status === 'CONCURRENCY_LIMIT') {
            console.log('Showing CONCURRENCY_LIMIT warning');
            message.warning(data.message, 7);
          } else if (data.status === 'BUSY') {
            console.log('Showing BUSY info');
            message.info(data.message, 5);
          } else {
            console.log(
              'Status does not match CONCURRENCY_LIMIT or BUSY:',
              data.status
            );
          }
        } else {
          console.log(
            'Message type does not match ai_call_status_notification:',
            data.type
          );
        }
      };

      ws.onerror = (error) => {
        console.error('AI Call Status WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log('AI Call Status WebSocket disconnected', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });
      };
    };

    connect();

    return () => {
      cancelled = true;
      signal.removeEventListener('abort', onAbort);
      wsRef.current?.close();
    };
  }, [token, decodedUserToken]);
}
